import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

import { errorIdFor } from '@shared/components/form-field/form-field';

export type TextInputType = 'text' | 'password' | 'email' | 'tel' | 'search' | 'number';

/**
 * Single-line text control for Reactive Forms.
 *
 * A ControlValueAccessor rather than a styled bare `<input>`, because the
 * password reveal toggle, the `aria-invalid` wiring and the error-id
 * convention would otherwise be retyped at every call site - and the one that
 * gets forgotten is the one that ships without a label association.
 *
 * State is signals, not fields: the app is zoneless, so a value written from
 * `writeValue()` during a `patchValue` would never repaint.
 */
@Component({
  selector: 'app-text-input',
  host: { class: 'ti' },
  template: `
    <input
      [id]="controlId()"
      [type]="renderedType()"
      [value]="value()"
      [disabled]="disabled()"
      [attr.name]="name() || controlId()"
      [attr.placeholder]="placeholder() || null"
      [attr.autocomplete]="autocomplete() || null"
      [attr.inputmode]="inputMode() || null"
      [attr.maxlength]="maxLength() ?? null"
      [attr.aria-invalid]="invalid() ? 'true' : null"
      [attr.aria-describedby]="invalid() ? errorId() : null"
      (input)="onInput($event)"
      (blur)="onBlur()"
    />

    @if (revealable()) {
      <!--
        Renders on the enclosing app-form-field's LABEL row, right-aligned -
        not inset in the field's trailing gutter, which cost 62px of a 372px
        control and put the pill in the same corner a password manager draws
        its own icon in.

        Kept AFTER the input in the DOM even though it paints above it, so Tab
        runs field -> toggle. The toggle acts on a value the user has already
        started typing; placing it first would make every keyboard user step
        past a secondary control to reach the box they came for.
      -->
      <button
        type="button"
        class="ti-eye"
        [attr.aria-label]="revealed() ? 'Hide password' : 'Show password'"
        [attr.aria-pressed]="revealed()"
        (click)="toggleReveal()"
      >
        <!--
          Two glyph states rather than one crossed-out icon: the slash is
          easy to miss at 13px, whereas the closed lid reads instantly.
        -->
        <span aria-hidden="true">{{ revealed() ? 'HIDE' : 'SHOW' }}</span>
      </button>
    }
  `,
  styles: [
    `
      /* The positioning context is declared on :host, not on the host CLASS.
         Emulated encapsulation rewrites a class selector to carry the
         _ngcontent attribute, which only elements INSIDE this template have -
         the host element carries _nghost instead. So the class form matched
         nothing at all, the absolutely positioned toggle found no positioned
         ancestor here, and it anchored to the nearest one it could find: the
         login panel, which parked the pill in that panel's top-right corner
         instead of on the field. display: block because a percentage offset
         needs a block containing block, and the host defaulted to inline. */
      :host {
        position: relative;
        display: block;
      }

      /* Right-aligned on the label row. The 6px is label.f's margin-bottom in
         src/styles/_components.scss - kept in step by hand, because the label
         belongs to app-form-field and this button does not. */
      .ti-eye {
        position: absolute;
        right: 0;
        bottom: calc(100% + var(--sp-3));
        padding: 2px var(--sp-3);
        border: 1px solid var(--line-soft);
        border-radius: var(--r-sm);
        background: transparent;
        color: var(--ink-faint);
        font-family: var(--mono);
        font-size: 8.5px;
        line-height: 1.35;
        letter-spacing: 0.12em;
        cursor: pointer;
        transition: 0.15s;
      }

      .ti-eye:hover {
        color: var(--gold);
        border-color: var(--gold);
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // forwardRef because the token is registered before the class binding
      // is initialised in the module scope.
      useExisting: forwardRef(() => TextInput),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextInput implements ControlValueAccessor {
  /** Must match the enclosing app-form-field's `controlId`, or the label binds to nothing. */
  readonly controlId = input.required<string>();

  readonly type = input<TextInputType>('text');
  readonly placeholder = input('');

  /**
   * Real autocomplete tokens matter here: `current-password` is what lets a
   * password manager fill the form, and an omitted token makes browsers guess.
   */
  readonly autocomplete = input('');

  readonly inputMode = input('');
  readonly name = input('');
  readonly maxLength = input<number | null>(null);

  /** Drives `aria-invalid` and the described-by link to the error paragraph. */
  readonly invalid = input(false);

  /**
   * Adds the SHOW/HIDE toggle, rendered on the label row of the enclosing
   * app-form-field. Only meaningful with type="password", and only correct
   * inside an app-form-field - the label row it aligns to is that component's.
   */
  readonly revealable = input(false);

  protected readonly value = signal('');
  protected readonly disabled = signal(false);
  protected readonly revealed = signal(false);

  protected readonly errorId = computed(() => errorIdFor(this.controlId()));

  /** Revealing swaps the element type, which is what actually shows the text. */
  protected readonly renderedType = computed<TextInputType>(() =>
    this.revealable() && this.revealed() ? 'text' : this.type(),
  );

  private onChangeFn: (value: string) => void = () => undefined;
  private onTouchedFn: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.value.set(value === null || value === undefined ? '' : String(value));
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChangeFn(next);
  }

  protected onBlur(): void {
    this.onTouchedFn();
  }

  protected toggleReveal(): void {
    this.revealed.update((shown) => !shown);
  }
}
